import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Play, Check, AlertCircle, FileText, Server, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { BACKEND_CONFIG } from '../config/backend';

const JsonReprocessorNew = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [isValidJson, setIsValidJson] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  // Validar JSON
  const validateJson = (jsonString) => {
    if (!jsonString.trim()) {
      setIsValidJson(true);
      return;
    }

    try {
      JSON.parse(jsonString);
      setIsValidJson(true);
    } catch (error) {
      setIsValidJson(false);
    }
  };

  // Formatar JSON
  const formatJson = () => {
    if (!isValidJson || !jsonInput.trim()) return;

    try {
      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonInput(formatted);
      toast.success('✅ JSON formatado com sucesso!');
    } catch (error) {
      toast.error('❌ Erro ao formatar JSON');
    }
  };

  // Processo completo CORRETO: Create + Reprocess
  const executeReprocessing = async () => {
    if (!jsonInput.trim()) {
      toast.error('Cole o JSON primeiro!');
      return;
    }

    if (!isValidJson) {
      toast.error('JSON inválido. Corrija os erros primeiro.');
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const jsonData = JSON.parse(jsonInput);

      console.log('🚀 Iniciando reprocessamento CORRETO...');
      toast.info('🚀 Iniciando reprocessamento (Create + Execute)...');

      const response = await fetch(`${BACKEND_CONFIG.BASE_URL}/api/reprocess/full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonData: jsonData
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult({
          success: true,
          message: 'Reprocessamento executado com sucesso!',
          details: `
✅ Passo 1: JSON criado no servidor
✅ Passo 2: Reprocessamento executado
⏰ Processado em: ${new Date(data.timestamp).toLocaleString('pt-BR')}
          `.trim(),
          steps: data.steps,
          timestamp: data.timestamp
        });

        toast.success('🎉 Reprocessamento concluído com sucesso!');
        console.log('✅ Resultado completo:', data);
      } else {
        throw new Error(data.error || 'Erro no reprocessamento');
      }

    } catch (error) {
      console.error('❌ Erro no reprocessamento:', error);
      
      setResult({
        success: false,
        message: 'Erro no reprocessamento',
        details: `❌ ${error.message}`,
        timestamp: new Date().toISOString()
      });
      
      toast.error(`❌ Erro: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Área de entrada do JSON */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800">JSON para Reprocessamento</h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={formatJson}
              disabled={!jsonInput.trim() || !isValidJson}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded-lg transition-colors"
            >
              Formatar
            </button>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              isValidJson 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isValidJson ? '✓ Válido' : '✗ Inválido'}
            </div>
          </div>
        </div>

        <textarea
          value={jsonInput}
          onChange={(e) => {
            setJsonInput(e.target.value);
            validateJson(e.target.value);
          }}
          placeholder={`Cole aqui o JSON completo para reprocessamento...

Exemplo:
{
  "nomePagador": "João Silva",
  "cobrancaID": "123456",
  "valor": 1500.00,
  ...
}`}
          className={`w-full h-64 p-4 border rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            isValidJson 
              ? 'border-gray-300' 
              : 'border-red-300 bg-red-50'
          }`}
        />
      </motion.div>

      {/* Botão de ação principal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex justify-center"
      >
        <button
          onClick={executeReprocessing}
          disabled={!jsonInput.trim() || !isValidJson || isProcessing}
          className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-3"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Reprocessando...</span>
            </>
          ) : (
            <>
              <Play className="h-5 w-5" />
              <span>Executar Reprocessamento Completo</span>
            </>
          )}
        </button>
      </motion.div>

      {/* Resultado */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl shadow-lg p-6 border ${
            result.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div className={`p-2 rounded-lg ${
              result.success 
                ? 'bg-green-500' 
                : 'bg-red-500'
            }`}>
              {result.success ? (
                <Check className="h-5 w-5 text-white" />
              ) : (
                <AlertCircle className="h-5 w-5 text-white" />
              )}
            </div>
            <div>
              <h3 className={`text-lg font-bold ${
                result.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.message}
              </h3>
              <p className="text-sm text-gray-600">
                {new Date(result.timestamp).toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg font-mono text-sm whitespace-pre-line ${
            result.success 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {result.details}
          </div>
        </motion.div>
      )}

      {/* Informações sobre o processo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 rounded-xl p-6 border border-blue-200"
      >
        <div className="flex items-center space-x-3 mb-3">
          <Server className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-bold text-blue-800">Como Funciona</h3>
        </div>
        
        <div className="space-y-2 text-sm text-blue-700">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span><strong>Passo 1:</strong> JSON é enviado para <code>POST /reprocess/create</code></span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span><strong>Passo 2:</strong> Reprocessamento é executado via <code>POST /reprocess</code></span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span><strong>Resultado:</strong> Processo completo executado automaticamente</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default JsonReprocessorNew;
